import { PropsWithChildren, ReactNode, forwardRef } from 'react';

import { makeStyles } from '@material-ui/core';

import { ScrollTopButton } from '../ScrollTopButton';
import { BackstagePage } from './BackstagePage';
import { Content } from './Content';
import { Header, HeaderProps } from './Header';
import { HeaderContent } from './HeaderContent';
import { NarrowBoxProps } from './NarrowBox';
import { SidebarContent, SidebarContentProps } from './SidebarContent';

const useStyles = makeStyles(theme => ({
  content: { paddingBottom: theme.spacing(12) },
}));

export type PageLayoutProps = PropsWithChildren &
  Pick<
    HeaderProps,
    | 'title'
    | 'headerBreadcrumbs'
    | 'headerTabs'
    | 'stickyHeader'
    | 'stickyHeaderTabs'
    | 'headerTabsRef'
    | 'isLoading'
    | 'pageTitleOverride'
    | 'enableMargin'
  > &
  Pick<SidebarContentProps, 'sideBarRight' | 'sideBarLeft'> & {
    narrow?: boolean | NarrowBoxProps['width'];
    headerContent?: ReactNode;
    scrollButton?: boolean;
    noPadding?: boolean;
    floatingHeader?: ReactNode;
    subheader?: ReactNode;
  };

export const PageLayout = forwardRef<HTMLDivElement, PageLayoutProps>(
  (
    {
      children,
      floatingHeader,
      sideBarRight,
      sideBarLeft,
      title,
      enableMargin,
      headerBreadcrumbs,
      headerTabs,
      headerContent,
      stickyHeader,
      stickyHeaderTabs,
      headerTabsRef,
      isLoading,
      pageTitleOverride,
      scrollButton = false,
      narrow,
      noPadding,
      subheader,
    },
    ref,
  ) => {
    const classes = useStyles();

    return (
      <BackstagePage ref={ref}>
        {floatingHeader}
        {scrollButton && <ScrollTopButton />}
        <Header
          enableMargin={enableMargin}
          title={title}
          subheader={subheader}
          isLoading={isLoading}
          pageTitleOverride={pageTitleOverride}
          narrow={narrow}
          headerBreadcrumbs={headerBreadcrumbs}
          headerTabs={headerTabs}
          stickyHeader={stickyHeader}
          stickyHeaderTabs={stickyHeaderTabs}
          headerTabsRef={headerTabsRef}
        >
          {headerContent ? (
            <HeaderContent>{headerContent}</HeaderContent>
          ) : null}
        </Header>

        <Content
          narrow={narrow}
          className={noPadding ? undefined : classes.content}
          noPadding={noPadding}
        >
          {sideBarLeft || sideBarRight ? (
            <SidebarContent
              sideBarLeft={sideBarLeft}
              sideBarRight={sideBarRight}
            >
              {children}
            </SidebarContent>
          ) : (
            children
          )}
        </Content>
      </BackstagePage>
    );
  },
);
